import { Check, Minus } from "@untitledui/icons";
import { type ComponentPropsWithoutRef, useEffect, useId, useRef } from "react";
import { Icon } from "../Icon";
import styles from "./Checkbox.module.css";

/**
 * Checkbox — tri-state checkbox primitive.
 *
 * Matches the Figma Checkbox component set (fileKey
 * QkVMIm6TUdblppfqYlUtai, node 1101:539). Single size (14×14px)
 * square shape — the Figma variant enum reserves `size` and `shape`
 * for future expansion but only Square/Small ships today.
 *
 * Tri-state model: `checked` + `indeterminate`. When `indeterminate`
 * is true, the box shows a minus glyph and the browser's native
 * `input.indeterminate` property is set via useEffect (no HTML
 * attribute exists for this — it must go through the DOM node). The
 * visual state takes precedence over `checked`: if both are set,
 * the minus glyph wins.
 *
 * Accessibility:
 *   - Renders a real `<input type="checkbox">` (sr-only positioned,
 *     opacity 0) so native keyboard (Space), click, form submission,
 *     and screen-reader semantics all work out of the box.
 *   - The custom 14×14 box is a `<span>` that uses CSS sibling
 *     selectors (`.input:checked + .box`, `.input:indeterminate +
 *     .box`, `.input:focus-visible + .box`) to render the visual
 *     state — no JS mirror of the input's state.
 *   - Focus-visible ring comes from the input's focus, not the
 *     wrapper label, so keyboard focus is distinct from label hover.
 *
 * Labelling:
 *   - If `label` is provided, the primitive renders a wrapping
 *     `<label>` around the input + box + visible text. Clicking the
 *     text toggles the checkbox via native <label>/<input>
 *     association.
 *   - If `label` is omitted, the consumer is responsible for a11y:
 *     pass `aria-label` (via rest spread) OR place the Checkbox
 *     inside a sibling <label htmlFor>.
 *
 * Colors:
 *   - Accent (checked/indeterminate fill): teal-600 (#0d9488) in
 *     light, teal-300 (#5eead4) in dark — matches --stroke-input-active
 *     from tokens-figma.css. Still hardcoded here pending a shared
 *     --accent-brand / --checkbox-accent token. Flagged in design-asks.
 *   - Disabled accent: neutral-400 (#a3a3a3) via --icon-muted — the
 *     same token used elsewhere for muted-state icon surfaces.
 */

export type CheckboxProps = {
  /** Whether the box is checked. `indeterminate` takes visual precedence. */
  checked: boolean;
  /**
   * When true, the box shows a minus glyph (via input.indeterminate) and
   * the accent fill regardless of `checked`. aria-state is "mixed".
   */
  indeterminate?: boolean;
  /** Fires with the new checked value when the user toggles the box. */
  onChange: (checked: boolean) => void;
  /** Visible text label. When omitted, consumer supplies aria-label via rest. */
  label?: string;
  className?: string;
} & Omit<
  ComponentPropsWithoutRef<"input">,
  "type" | "checked" | "onChange" | "className" | "aria-checked"
>;

export function Checkbox({
  checked,
  indeterminate = false,
  onChange,
  label,
  disabled,
  id,
  className,
  ...rest
}: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);

  // Native <input> has no `indeterminate` HTML attribute — it's a DOM
  // property set via JS. This effect keeps the property in sync with
  // the `indeterminate` prop whenever it (or `checked`) changes.
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const wrapperClasses = [styles.wrapper, className].filter(Boolean).join(" ");

  return (
    <label className={wrapperClasses} htmlFor={inputId}>
      <input
        ref={inputRef}
        id={inputId}
        type="checkbox"
        className={styles.input}
        checked={checked}
        // When indeterminate, explicitly announce "mixed" to AT.
        // Firefox / WebKit don't reliably reflect the native DOM
        // `indeterminate` property onto the AT's aria-checked state
        // (see w3c/aria#1622), so relying only on the useEffect that
        // sets input.indeterminate can leave screen readers
        // announcing the tri-state as plain checked/unchecked.
        // When NOT indeterminate we omit the override and let native
        // <input type="checkbox"> manage aria-checked from `checked`.
        aria-checked={indeterminate ? "mixed" : undefined}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        {...rest}
      />
      <span className={styles.box} aria-hidden="true">
        {indeterminate ? (
          <Icon as={Minus} size="xs" tone="inherit" />
        ) : checked ? (
          <Icon as={Check} size="xs" tone="inherit" />
        ) : null}
      </span>
      {label && <span className={styles.label}>{label}</span>}
    </label>
  );
}
